import { PaperClipOutlined } from '@ant-design/icons';
import { Spin, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { TablePaginationConfig } from 'antd/lib/table/interface';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import ILoan from '~Api/Loan/ILoan';
import ILoanEmail from '~Api/LoanEmail/ILoanEmail';
import { loanGetAction } from '~Loans/actions';
import Layout from '~Loans/Loan/Layout';
import { loanSelector } from '~Loans/selectors';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import ModalStateEnum from '~UI/ModalStateEnum';
import PersonLink from './PersonLink';
import './emails.less';
import { loanEmailsListAction } from './actions';
import EmailModal from './EmailModal';
import {
    loanEmailsCountSelector,
    loanEmailsPageNumberSelector,
    loanEmailsPageSelector,
} from './selectors';

const defaultPerPage: number = 20;

export default function Emails(): ReactElement {
    const { loanUuid } = useParams<{ loanUuid: string }>();

    const [ loanEmailUuid, setLoanEmailUuid ] = useState<string>(null);
    const [ emailModalState, setEmailModalState ] = useState<ModalStateEnum>(ModalStateEnum.Off);

    const loan: ILoan = useSelector((state: IGlobalState): ILoan => loanSelector(state, loanUuid));
    const emails: IDictionary<ILoanEmail> = useSelector((state: IGlobalState): IDictionary<ILoanEmail> => loanEmailsPageSelector(state, loanUuid));
    const currentPage: number = useSelector((state: IGlobalState): number => loanEmailsPageNumberSelector(state, loanUuid));
    const totalCount: number = useSelector((state: IGlobalState): number => loanEmailsCountSelector(state, loanUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!loan) {
            dispatch(loanGetAction(loanUuid));
        }
    }, [
        dispatch,
        loan,
        loanUuid,
    ]);

    useEffect((): void => {
        setEmailModalState(ModalStateEnum.Off);
        setLoanEmailUuid(null);
        dispatch(loanEmailsListAction(loanUuid, 1, defaultPerPage));
    }, [
        dispatch,
        loanUuid,
    ]);

    const onChangeTable: (pagination: TablePaginationConfig) => void = useCallback((pagination: TablePaginationConfig): void => {
        setEmailModalState(ModalStateEnum.Off);
        setLoanEmailUuid(null);
        dispatch(loanEmailsListAction(loanUuid, pagination.current, defaultPerPage));
    }, [
        dispatch,
        loanUuid,
    ]);

    const onOpenEmailModal: (loanEmailUuid: string) => void = useCallback((loanEmailUuid: string): void => {
        setLoanEmailUuid(loanEmailUuid);
        setEmailModalState(ModalStateEnum.Open);
    }, []);

    const onCloseEmailModal: () => void = useCallback((): void => {
        setEmailModalState(ModalStateEnum.Closed);
    }, []);

    const columns: ColumnsType<ILoanEmail> = [
        {
            className: 'sender',
            key: 'sender',
            render: (email: ILoanEmail): ReactNode => (
                <>
                    <PersonLink
                        email={email.senderEmail}
                        name={email.senderName}
                    />
                    <Typography.Text
                        className='date'
                        type='secondary'
                    >
                        {dayjs(email.sentTime).format('dddd, D MMMM YYYY [at] h:mma')}
                    </Typography.Text>
                </>
            ),
            title: 'From',
            width: 320,
        },
        {
            className: 'subject',
            key: 'subject',
            render: (email: ILoanEmail): ReactNode => {
                const onClick: () => void = (): void => onOpenEmailModal(email.uuid);

                const attachmentIconBlock: ReactElement = email.attachments.length > 0 && (
                    <PaperClipOutlined />
                );

                const emailModalBlock: ReactElement = (
                    loanEmailUuid === email.uuid
                    && emailModalState !== ModalStateEnum.Off
                ) && (
                    <EmailModal
                        isOpen={emailModalState === ModalStateEnum.Open}
                        loanEmailUuid={email.uuid}
                        onCancel={onCloseEmailModal}
                    />
                );

                return (
                    <>
                        <a
                            className='subject'
                            onClick={onClick}
                        >
                            {attachmentIconBlock}
                            {email.subject || '-'}
                        </a>
                        <Typography.Text
                            className='summary'
                            ellipsis={true}
                        >
                            {_.trim(email.summary) || '-'}
                        </Typography.Text>
                        {emailModalBlock}
                    </>
                );
            },
            title: 'Subject',
        },
    ];

    const pagination: TablePaginationConfig = {
        current: currentPage,
        pageSize: defaultPerPage,
        showSizeChanger: false,
        total: totalCount,
    };

    if (!loan) {
        return (
            <Layout
                section='emails'
                uuid={loanUuid}
            >
                <Helmet>
                    <title>Emails</title>
                </Helmet>
                <Typography.Title level={2}>Emails</Typography.Title>
                <Spin />
            </Layout>
        );
    }

    return (
        <Layout
            section='emails'
            uuid={loanUuid}
        >
            <Helmet>
                <title>Emails - {loan.code} - {loan.name}</title>
            </Helmet>
            <Typography.Title level={2}>Emails</Typography.Title>
            <Table
                columns={columns}
                dataSource={_.values(emails)}
                loading={!emails}
                onChange={onChangeTable}
                pagination={pagination}
                rowKey='uuid'
                size='middle'
            />
        </Layout>
    );
}
